import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLock,
    faSave,
    faSpinner,
    faUndo,
    faBold,
    faItalic,
    faShieldAlt,
    faCheck,
    faExclamationTriangle,
    faPen,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { formatWhatsAppText } from '../../../TicketDetailView/utils';

const QUICK_EMOJIS = ['👋', '😊', '✨', '👍', '🙏', '🙌', '⭐'];

const WelcomeMessageEditor = ({
    isDark,
    prefixText,
    customMessage,
    setCustomMessage,
    isPersonalized,
    profanityValidation,
    hasChanges,
    canSave,
    isSaving,
    isEditing,
    setIsEditing,
    onSave,
    onCancel,
    onClear
}) => {
    const textareaRef = useRef(null);

    // Inserción en textarea
    const insertAtCursor = (textToInsert) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            setCustomMessage(prev => (prev + textToInsert).slice(0, 500));
            return;
        }

        const start = textarea.selectionStart ?? customMessage.length;
        const end = textarea.selectionEnd ?? customMessage.length;
        const nextVal = (customMessage.substring(0, start) + textToInsert + customMessage.substring(end)).slice(0, 500);
        setCustomMessage(nextVal);

        setTimeout(() => {
            textarea.focus();
            const newPos = Math.min(start + textToInsert.length, 500);
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    // Formato Negrita
    const handleBold = () => {
        const textarea = textareaRef.current;
        if (!textarea) {
            insertAtCursor('*texto*');
            return;
        }
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start !== end) {
            const selected = customMessage.substring(start, end);
            insertAtCursor(`*${selected}*`);
        } else {
            insertAtCursor('*texto*');
        }
    };

    // Formato Cursiva
    const handleItalic = () => {
        const textarea = textareaRef.current;
        if (!textarea) {
            insertAtCursor('_texto_');
            return;
        }
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start !== end) {
            const selected = customMessage.substring(start, end);
            insertAtCursor(`_${selected}_`);
        } else {
            insertAtCursor('_texto_');
        }
    };

    return (
        <div
            className="card border shadow-sm rounded-3 h-100"
            style={{
                backgroundColor: isDark ? '#121e2d' : '#ffffff',
                borderColor: isDark ? '#233142' : '#e2e8f0'
            }}
        >
            {/* Header del Card */}
            <div
                className="card-header py-3 px-4 d-flex justify-content-between align-items-center border-bottom flex-wrap gap-2"
                style={{
                    backgroundColor: isDark ? '#162232' : '#f8fafc',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold small" style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}>
                        {isEditing ? 'Editor de Saludo Personal' : 'Tu Saludo de Bienvenida'}
                    </span>
                </div>

                {/* Badge de Estado */}
                {isPersonalized ? (
                    <span
                        className="badge rounded-pill px-2.5 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.74rem',
                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.18)' : '#dcfce7',
                            color: isDark ? '#6ee7b7' : '#166534',
                            border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.35)' : '#bbf7d0'}`
                        }}
                    >
                        <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.68rem' }} />
                        <span>Personalizado para ti</span>
                    </span>
                ) : (
                    <span
                        className="badge rounded-pill px-2.5 py-1 fw-semibold"
                        style={{
                            fontSize: '0.74rem',
                            backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                            color: isDark ? '#94a3b8' : '#64748b',
                            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`
                        }}
                    >
                        Solo prefijo institucional
                    </span>
                )}
            </div>

            <div className="card-body p-4 d-flex flex-column">
                {/* Prefijo Institucional Fijo */}
                <div className="mb-3">
                    <label
                        className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1.5"
                        style={{ color: isDark ? '#94a3b8' : '#64748b' }}
                    >
                        <FontAwesomeIcon icon={faLock} className="text-warning" style={{ fontSize: '0.8rem' }} />
                        <span>Prefijo Institucional (Fijo - Se antepone siempre al cliente):</span>
                    </label>
                    <div
                        className="p-2.5 px-3 rounded-2 border d-flex align-items-start justify-content-between gap-2"
                        style={{
                            backgroundColor: isDark ? 'rgba(37, 99, 235, 0.08)' : '#eff6ff',
                            borderColor: isDark ? 'rgba(37, 99, 235, 0.25)' : '#bfdbfe',
                            color: isDark ? '#93c5fd' : '#1e40af',
                            fontSize: '0.84rem',
                            lineHeight: '1.5'
                        }}
                    >
                        <span>{prefixText}</span>
                        <span
                            className="badge d-inline-flex align-items-center gap-1 flex-shrink-0"
                            style={{
                                backgroundColor: 'transparent',
                                color: isDark ? '#93c5fd' : '#1e40af',
                                fontSize: '0.72rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faShieldAlt} /> Fijo
                        </span>
                    </div>
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.74rem' }}>
                        Este mensaje institucional ya saluda y presenta tu nombre ante el cliente.
                    </small>
                </div>

                {!isEditing ? (
                    <>
                        {/* Vista de Lectura del Mensaje Adicional */}
                        <div className="mb-4">
                            <label
                                className="form-label small fw-semibold mb-1.5 d-flex justify-content-between align-items-center"
                                style={{ color: isDark ? '#cbd5e1' : '#334155' }}
                            >
                                <span>Mensaje adicional o despedida configurado:</span>
                                {customMessage && customMessage.trim() && (
                                    <span className="font-monospace text-muted" style={{ fontSize: '0.74rem' }}>
                                        {customMessage.length} caracteres
                                    </span>
                                )}
                            </label>
                            {customMessage && customMessage.trim() ? (
                                <div
                                    className="p-3 rounded-3 border"
                                    style={{
                                        backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                                        borderColor: isDark ? '#233142' : '#e2e8f0',
                                        color: isDark ? '#f1f5f9' : '#1e293b',
                                        fontSize: '0.88rem',
                                        lineHeight: '1.6',
                                        whiteSpace: 'pre-wrap',
                                        minHeight: '80px'
                                    }}
                                >
                                    {formatWhatsAppText(customMessage)}
                                </div>
                            ) : (
                                <div
                                    className="p-3 rounded-3 border text-center"
                                    style={{
                                        backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                                        borderColor: isDark ? '#233142' : '#e2e8f0',
                                        color: isDark ? '#94a3b8' : '#64748b',
                                        fontSize: '0.85rem',
                                        minHeight: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <span className="fst-italic">
                                        No has configurado ningún texto adicional. Se enviará únicamente el prefijo institucional por defecto.
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Footer Vista Lectura con Botón Editar */}
                        <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ borderColor: isDark ? '#233142' : '#f1f5f9' }}>
                            <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                                Vista de lectura
                            </span>
                            <button
                                type="button"
                                className="btn btn-outline-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2 fw-semibold"
                                onClick={() => setIsEditing(true)}
                                style={{ fontSize: '0.86rem' }}
                            >
                                <FontAwesomeIcon icon={faPen} />
                                <span>Editar Saludo</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Barra de Formato y Textarea */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1.5">
                                <label
                                    className="form-label small fw-semibold mb-0"
                                    style={{ color: isDark ? '#cbd5e1' : '#334155' }}
                                >
                                    Mensaje adicional o despedida (Opcional):
                                </label>
                                <span
                                    className="font-monospace small text-muted"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    {customMessage.length}/500 caracteres
                                </span>
                            </div>

                            {/* Toolbar */}
                            <div
                                className="p-1.5 rounded-top border border-bottom-0 d-flex align-items-center justify-content-between flex-wrap gap-1"
                                style={{
                                    backgroundColor: isDark ? '#162232' : '#f1f5f9',
                                    borderColor: isDark ? '#233142' : '#cbd5e1'
                                }}
                            >
                                <div className="d-flex align-items-center gap-1">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-1 px-2 text-decoration-none shadow-none"
                                        onClick={handleBold}
                                        style={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.82rem' }}
                                        title="Negrita (*texto*)"
                                    >
                                        <FontAwesomeIcon icon={faBold} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-1 px-2 text-decoration-none shadow-none"
                                        onClick={handleItalic}
                                        style={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.82rem' }}
                                        title="Cursiva (_texto_)"
                                    >
                                        <FontAwesomeIcon icon={faItalic} />
                                    </button>

                                    <div className="vr mx-1 opacity-25" style={{ height: '16px' }} />

                                    {QUICK_EMOJIS.map(emoji => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            className="btn btn-sm p-0 px-1 border-0 bg-transparent shadow-none"
                                            onClick={() => insertAtCursor(emoji)}
                                            style={{ fontSize: '0.95rem' }}
                                            title={`Insertar ${emoji}`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>

                                {customMessage.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 px-2 text-decoration-none shadow-none text-muted"
                                        onClick={onClear}
                                        style={{ fontSize: '0.74rem' }}
                                        title="Limpiar texto"
                                    >
                                        <FontAwesomeIcon icon={faUndo} className="me-1" />
                                        <span>Limpiar</span>
                                    </button>
                                )}
                            </div>

                            {/* Textarea */}
                            <textarea
                                ref={textareaRef}
                                rows="4"
                                className="form-control rounded-bottom rounded-0 mb-1"
                                placeholder="Por defecto está vacío porque el prefijo ya saluda y da la bienvenida. Si deseas, agrega aquí indicaciones adicionales, por ejemplo: ¿En qué te puedo ayudar hoy?"
                                value={customMessage}
                                onChange={e => setCustomMessage(e.target.value.slice(0, 500))}
                                style={{
                                    backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                    borderColor: isDark ? '#233142' : '#cbd5e1',
                                    color: isDark ? '#f1f5f9' : '#1e293b',
                                    fontSize: '0.88rem',
                                    lineHeight: '1.6'
                                }}
                            />
                        </div>

                        {/* Alerta de Palabras Inapropiadas */}
                        {!profanityValidation.isValid && (
                            <div
                                className="alert alert-danger d-flex align-items-center gap-2 p-2.5 mb-3 rounded-3 border"
                                style={{
                                    fontSize: '0.82rem',
                                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                                    borderColor: isDark ? 'rgba(239, 68, 68, 0.35)' : '#fecaca',
                                    color: isDark ? '#fca5a5' : '#b91c1c'
                                }}
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                                <span>
                                    <strong>Palabra no permitida:</strong> Se detectó el término <code>"{profanityValidation.foundWord}"</code>. No está permitido guardar mensajes con palabras obscenas o inapropiadas.
                                </span>
                            </div>
                        )}

                        {/* Footer Guardar y Cancelar */}
                        <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ borderColor: isDark ? '#233142' : '#f1f5f9' }}>
                            <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                                {hasChanges ? 'Hay cambios sin guardar' : 'Todo al día'}
                            </span>

                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary rounded-pill px-3 shadow-none d-flex align-items-center gap-1.5"
                                    onClick={onCancel}
                                    disabled={isSaving}
                                    style={{ fontSize: '0.86rem' }}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>Cancelar</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2 fw-semibold"
                                    onClick={onSave}
                                    disabled={!canSave}
                                    style={{ fontSize: '0.86rem' }}
                                >
                                    {isSaving ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Guardando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>Guardar mi Mensaje</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WelcomeMessageEditor;
